export interface CtaStateInput {
    /** Marketplace, in stock, selected variant purchasable. */
    isBuyable: boolean;
    /** Sample can be purchased (independent of main buyability / EcommerceReady). */
    isSampleBuyable: boolean;
    /** Pricing type is ecommercePricing. */
    isMarketplace: boolean;
    /** The product has colours / materials / sizes to pick in the drawer. */
    hasVariantOptions: boolean;
    /**
     * The enquiry CTA label from the professional's settings (e.g. "Request
     * Price / Brochure"). Used for the enquiry-led state instead of hardcoded
     * "Enquire" / "Request Quote". Defaults to a generic label when absent.
     */
    enquiryCTALabel?: string;
    /**
     * The professional's enquiry CTA may show (audience gating). On commerce
     * states this inverts the hierarchy: enquiry leads and commerce moves to
     * the "available on ArchiPro" line. Commerce only leads when enquiries
     * are off.
     */
    enquiryVisible: boolean;
}

/** The primary CTA label. Shop Now / Shop Sample for transactional states; an
 * arbitrary professional label for enquiry-led states. */
export type CtaLabel = string;

export interface CtaState {
    label: CtaLabel;
    /** Enquire / Request Quote — no transaction, an enquiry instead. */
    isEnquiryCTA: boolean;
    /**
     * Enquiry-led with nothing to configure: open the enquiry modal directly.
     * Otherwise the primary CTA opens the purchase drawer (Shop Now / Shop
     * Sample transact there; enquiry-led with variants picks a variant first,
     * which the drawer's Request Quote folds into the enquiry).
     */
    opensEnquiryModalDirectly: boolean;
    /**
     * A buyable/sample product whose professional takes enquiries leads with
     * Enquire (the QDD action); `label` and the drawer stay behind the shop
     * line instead of the primary slot.
     */
    enquiryLeads: boolean;
}

/**
 * Resolve the product detail primary CTA label and what it should open, from the
 * product's commerce state. Pure so the routing is unit-testable in isolation.
 */
export const resolveCtaState = ({
    isBuyable,
    isSampleBuyable,
    isMarketplace,
    hasVariantOptions,
    enquiryCTALabel,
    enquiryVisible,
}: CtaStateInput): CtaState => {
    const isEnquiryCTA = !isBuyable && !isSampleBuyable;

    // Transactional states use fixed labels; enquiry-led uses the professional's
    // configured label (e.g. "Request Price / Brochure") falling back to the
    // legacy defaults so nothing breaks when the value is absent.
    const label: CtaLabel = isBuyable
        ? 'Shop Now'
        : isSampleBuyable
          ? 'Shop Sample'
          : (enquiryCTALabel ?? (isMarketplace ? 'Enquire' : 'Request Quote'));

    return {
        label,
        isEnquiryCTA,
        opensEnquiryModalDirectly: isEnquiryCTA && !hasVariantOptions,
        enquiryLeads: enquiryVisible && !isEnquiryCTA,
    };
};
